import { useState } from "react";
import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { ChevronRight, ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/guides/running-a-building-business-uk/";

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Running a Building Business in the UK: The Complete Guide (2026)",
  "description": "A complete guide to running a building business in the UK: business setup, insurance, HMRC Making Tax Digital, managing subcontractors, quoting accurately, and cash flow management.",
  "url": PAGE_URL,
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com",
  },
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Guides", "item": "https://thepropertool.com/guides/" },
    { "@type": "ListItem", "position": 3, "name": "Running a Building Business UK", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What insurance does a builder need in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "UK builders need at minimum: public liability insurance (typically £2m–£10m cover); employers' liability insurance (legally required as soon as you hire anyone, including labour-only subcontractors in some cases); and professional indemnity insurance if you provide design or specification advice. Tool and plant insurance, contract works insurance, and commercial vehicle insurance are also strongly advisable. Many domestic clients and local authorities now require evidence of at least £5m public liability cover before allowing work to start.",
      },
    },
    {
      "@type": "Question",
      "name": "Does a builder need to register for VAT in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "UK builders must register for VAT once their taxable turnover in any 12-month period exceeds the VAT registration threshold (£90,000 as of 2026). Most residential building work is standard-rated at 20%, but certain work qualifies for the reduced 5% rate: including conversions from non-residential to residential use: and some new-build work is zero-rated. The VAT treatment of construction work is complex; always confirm with an accountant before pricing jobs that may be on the boundary.",
      },
    },
    {
      "@type": "Question",
      "name": "What is HMRC Making Tax Digital (MTD) and does it apply to builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Making Tax Digital (MTD) is HMRC's programme requiring businesses to keep digital tax records and submit VAT returns using compatible software. MTD for VAT already applies to all VAT-registered businesses. MTD for Income Tax Self Assessment (ITSA) is being rolled out from April 2026, starting with sole traders and landlords with income over £50,000. Builders who are VAT-registered or above the ITSA income threshold need compatible accounting software: HMRC maintains a list of approved tools.",
      },
    },
    {
      "@type": "Question",
      "name": "How do you manage subcontractors as a UK builder?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Managing subcontractors effectively requires: verifying CIS registration before they start (and deducting CIS tax at source if they're not gross-status registered); issuing written scopes of work and price agreements before work starts; running a weekly schedule to coordinate trades and avoid costly gaps on site; retaining a proportion of each payment (typically 2.5–5%) until the works are complete and defect-free; and understanding your liability as the main contractor for their work, their workers, and any materials they supply.",
      },
    },
    {
      "@type": "Question",
      "name": "How do UK builders manage cash flow?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cash flow management for UK builders depends on three things: getting paid on time, managing materials spend, and knowing your running cost commitments. Key practices include: always taking a deposit (typically 10–25% of job value) before starting; agreeing stage payments tied to clear milestones; invoicing promptly at each stage; keeping a 3-month forward look at committed costs vs expected income; and maintaining a cash buffer equal to at least 2–3 months of overhead. Slow-paying clients are one of the most common causes of otherwise profitable small building businesses failing.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What insurance does a builder need in the UK?",
    a: "UK builders need at minimum: public liability insurance (typically £2m–£10m cover); employers' liability insurance (legally required as soon as you hire anyone, including labour-only subcontractors in some cases); and professional indemnity insurance if you provide design or specification advice. Tool and plant insurance, contract works insurance, and commercial vehicle insurance are also strongly advisable. Many domestic clients and local authorities now require evidence of at least £5m public liability cover before allowing work to start.",
  },
  {
    q: "Does a builder need to register for VAT in the UK?",
    a: "UK builders must register for VAT once their taxable turnover in any 12-month period exceeds the VAT registration threshold (£90,000 as of 2026). Most residential building work is standard-rated at 20%, but certain work qualifies for the reduced 5% rate: including conversions from non-residential to residential use: and some new-build work is zero-rated. The VAT treatment of construction work is complex; always confirm with an accountant before pricing jobs that may be on the boundary.",
  },
  {
    q: "What is HMRC Making Tax Digital (MTD) and does it apply to builders?",
    a: "Making Tax Digital (MTD) is HMRC's programme requiring businesses to keep digital tax records and submit VAT returns using compatible software. MTD for VAT already applies to all VAT-registered businesses. MTD for Income Tax Self Assessment (ITSA) is being rolled out from April 2026, starting with sole traders and landlords with income over £50,000. Builders who are VAT-registered or above the ITSA income threshold need compatible accounting software: HMRC maintains a list of approved tools.",
  },
  {
    q: "How do you manage subcontractors as a UK builder?",
    a: "Managing subcontractors effectively requires: verifying CIS registration before they start (and deducting CIS tax at source if they're not gross-status registered); issuing written scopes of work and price agreements before work starts; running a weekly schedule to coordinate trades and avoid costly gaps on site; retaining a proportion of each payment (typically 2.5–5%) until the works are complete and defect-free; and understanding your liability as the main contractor for their work, their workers, and any materials they supply.",
  },
  {
    q: "How do UK builders manage cash flow?",
    a: "Cash flow management for UK builders depends on three things: getting paid on time, managing materials spend, and knowing your running cost commitments. Key practices include: always taking a deposit (typically 10–25% of job value) before starting; agreeing stage payments tied to clear milestones; invoicing promptly at each stage; keeping a 3-month forward look at committed costs vs expected income; and maintaining a cash buffer equal to at least 2–3 months of overhead. Slow-paying clients are one of the most common causes of otherwise profitable small building businesses failing.",
  },
];

const TOC = [
  { id: "business-setup", label: "Setting up your building business" },
  { id: "insurance", label: "Insurance requirements" },
  { id: "hmrc-mtd", label: "HMRC Making Tax Digital (MTD)" },
  { id: "subcontractors", label: "Managing subcontractors" },
  { id: "quoting-cash-flow", label: "Quoting and cash flow" },
  { id: "related-resources", label: "Related guides and tools" },
  { id: "faq", label: "Frequently asked questions" },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/60 pb-4 last:border-0 last:pb-0">
      <button
        className="w-full flex items-start justify-between gap-4 py-4 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="font-semibold text-base leading-snug">{q}</span>
        <ChevronDown className={`h-5 w-5 flex-shrink-0 text-muted-foreground mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-muted-foreground leading-relaxed pb-2">{a}</p>}
    </div>
  );
}

export default function GuideRunningABuildingBusinessUKPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Running a Building Business in the UK: The Complete Guide (2026)"
        description="A practical guide to running a building business in the UK: setting up as a sole trader or limited company, insurance, HMRC Making Tax Digital, managing subcontractors, and cash flow."
        canonical="/guides/running-a-building-business-uk"
        schema={[WEBPAGE_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-muted-foreground">Guides</span>
              <ChevronRight className="h-4 w-4" />
              <span>Running a Building Business UK</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Complete Guide · 2026</span>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
              Running a building business in the UK: the complete guide
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl">
              Business setup, insurance, HMRC Making Tax Digital, managing subcontractors, quoting accurately, and keeping cash flow under control. Everything you need to run a sustainable building business in the UK.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" asChild>
                <Link href="/login">
                  Try The Proper Tool free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#business-setup">Jump to the guide</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · No credit card required</p>
          </div>
        </section>

        {/* TOC */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">In this guide</p>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
              {TOC.map((item, i) => (
                <li key={item.id} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground/60 w-4 text-right flex-shrink-0">{i + 1}.</span>
                  <a href={`#${item.id}`} className="text-sm text-primary hover:underline">
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Section 1: Business setup */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="business-setup">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">1. Setting up your building business</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Most UK builders start out as sole traders. It's the simplest structure: you register with HMRC for Self Assessment, keep records of your income and expenses, and pay income tax and National Insurance on your profits. There's no registration with Companies House, no separate business bank account required by law (though strongly advisable in practice), and no annual accounts filing.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              As the business grows: particularly once your annual profit consistently exceeds around £30,000–£40,000: many builders incorporate as a limited company. A limited company pays Corporation Tax (currently 19–25% depending on profits) rather than income tax, which can be more tax-efficient at higher earnings. It also limits your personal liability: if the company gets into trouble, your personal assets are not automatically at risk. The trade-off is more administrative overhead: annual accounts, a confirmation statement, payroll, and directors' responsibilities under the Companies Act.
            </p>

            <h3 className="text-xl font-semibold mb-4">Sole trader vs limited company: the key differences</h3>
            <div className="overflow-x-auto rounded-xl border border-border mb-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 font-semibold w-1/3"></th>
                    <th className="text-left px-4 py-3 font-semibold">Sole trader</th>
                    <th className="text-left px-4 py-3 font-semibold">Limited company</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { aspect: "Setup", sole: "Register with HMRC for Self Assessment", limited: "Register with Companies House (£50 online)" },
                    { aspect: "Tax on profits", sole: "Income tax (20–45%) + National Insurance", limited: "Corporation tax (19–25%) on profits; income tax on salary/dividends" },
                    { aspect: "Admin", sole: "Annual Self Assessment tax return", limited: "Annual accounts, confirmation statement, payroll" },
                    { aspect: "Personal liability", sole: "Unlimited: your personal assets are at risk", limited: "Limited: personal assets generally protected" },
                    { aspect: "CIS", sole: "Register as a subcontractor with HMRC CIS", limited: "Register the company as a subcontractor; additional CIS admin" },
                    { aspect: "Best for", sole: "Starting out, lower profits, simpler jobs", limited: "Growing businesses, higher profits, larger contracts" },
                  ].map((row) => (
                    <tr key={row.aspect} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-3 font-medium">{row.aspect}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.sole}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.limited}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold mb-3">The Construction Industry Scheme (CIS)</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If you work as a subcontractor for other contractors, you must register for the Construction Industry Scheme. Your contractor will deduct CIS tax at source: either 20% if you are registered, or 30% if you are not. This is not a separate tax; it's a payment on account of your income tax, which is reconciled through your Self Assessment return.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If you take on subcontractors yourself, you become a contractor for CIS purposes and must verify their CIS status, make the correct deductions, and file monthly CIS returns with HMRC. Failing to do this correctly can result in significant penalties.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Builders with a good compliance record can apply for gross payment status, meaning no deductions are taken at source. HMRC requires a minimum turnover and tax compliance history. It is worth pursuing once your business is established: the cash flow benefit is meaningful.
            </p>
          </div>
        </section>

        {/* Section 2: Insurance */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="insurance">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">2. Insurance requirements for UK builders</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              The insurance landscape for UK builders has three non-negotiable categories and several others that are advisable depending on the size and nature of your work.
            </p>

            <div className="space-y-6">
              {[
                {
                  title: "Public liability insurance",
                  required: true,
                  body: "This covers you if your work causes injury to a third party or damage to their property. It is not a legal requirement, but it is a practical one: most domestic clients and all commercial clients will ask for a certificate before you start. The standard minimum for residential work is £2 million; for local authorities and larger commercial contracts, £5 million or £10 million is common. Annual premiums typically run £300–£800 for sole traders on residential work, though claims history, turnover, and work type all affect the figure.",
                },
                {
                  title: "Employers' liability insurance",
                  required: true,
                  body: "This is a legal requirement as soon as you have employees. The statutory minimum is £5 million. Importantly, 'employees' for this purpose can include labour-only subcontractors who work under your direction: not just people on your payroll. Failure to hold adequate employers' liability insurance is a criminal offence and carries significant fines. HMRC and HSE can check.",
                },
                {
                  title: "Professional indemnity insurance",
                  required: false,
                  body: "Professional indemnity (PI) covers you for claims arising from advice or designs you provide. If you specify materials, design elements, or make recommendations that turn out to be wrong, PI covers your legal defence and any damages. It's increasingly required on design-and-build contracts and by clients who ask you to specify as well as build. Cover from £100,000 is available; the level needed depends on the value and complexity of work where you provide professional input.",
                },
                {
                  title: "Contract works insurance",
                  required: false,
                  body: "This covers the works under construction against damage, theft, or accidental loss: including materials on site before they're installed. It's distinct from public liability (which covers damage to third parties) and from the client's property insurance (which does not cover works in progress). On larger contracts, the client may specify who holds this cover.",
                },
                {
                  title: "Tools and plant insurance",
                  required: false,
                  body: "Tool theft from vans and sites is common. Standard van insurance policies typically exclude tools; you need a separate tools-in-transit policy or a business contents policy covering your kit. For hired plant (scaffolding, MEWPs, specialist equipment), check whether your hire agreement requires you to insure it: most do.",
                },
              ].map((item) => (
                <div key={item.title} className="bg-background rounded-xl border border-border p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-semibold text-base">{item.title}</h3>
                    {item.required && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Essential</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: HMRC MTD */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="hmrc-mtd">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">3. HMRC Making Tax Digital (MTD) for builders</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Making Tax Digital (MTD) is HMRC's multi-year programme requiring businesses to keep digital tax records and file returns using HMRC-compatible software. For builders, it has two main phases that matter:
            </p>

            <div className="space-y-6 mb-8">
              <div className="p-6 rounded-xl border border-border bg-background">
                <h3 className="font-semibold text-base mb-2">MTD for VAT (already mandatory)</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  All VAT-registered businesses must keep digital VAT records and submit VAT returns via compatible software. This has applied since April 2019 (for most businesses) and April 2022 (for all). If you are VAT-registered and still submitting VAT returns manually through the HMRC portal, you are not compliant and risk penalties. Compatible software includes Xero, QuickBooks, Sage, and many others: HMRC publishes a full list.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-border bg-background">
                <h3 className="font-semibold text-base mb-2">MTD for Income Tax Self Assessment (ITSA): rolling out from April 2026</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                  MTD for ITSA requires sole traders and landlords with qualifying income above the threshold to keep digital records and submit quarterly income and expenses updates to HMRC, replacing the current annual Self Assessment return.
                </p>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>From April 2026:</strong> Sole traders and landlords with income over £50,000</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>From April 2027:</strong> Income over £30,000</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>From April 2028:</strong> Income over £20,000 (subject to confirmation)</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-3">What this means for builders in practice</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If you are a sole trader builder turning over more than £50,000 (not profit: turnover), you need to be using MTD-compatible accounting software before April 2026. Quarterly submission means four updates per year plus a final end-of-year declaration, replacing the single annual Self Assessment return.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-3">
              The key practical change is that you need to maintain a running record of income and expenses throughout the year: not just pull everything together in January. For builders already using software like Xero or QuickBooks, this is largely already happening. For builders still running on spreadsheets and bank statements, it requires a change in approach.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              HMRC's guidance and the list of approved MTD-compatible software is on gov.uk. The transition timeline has shifted several times; always verify current deadlines directly with HMRC or your accountant.
            </p>
          </div>
        </section>

        {/* Section 4: Managing subcontractors */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="subcontractors">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">4. Managing subcontractors as a UK builder</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Most residential builders in the UK run their projects using a mix of directly employed labour and subcontractors. Managing subs effectively is one of the biggest levers on job profitability and programme: and one of the areas where things most commonly go wrong.
            </p>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-3">CIS compliance</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  When you engage subcontractors on construction work, you are a contractor for CIS purposes. Before they start, you must verify their CIS status with HMRC using the CIS online service. The deduction rate is:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground mb-2">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /><span><strong>0%</strong> for subcontractors with gross payment status</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /><span><strong>20%</strong> for registered subcontractors</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /><span><strong>30%</strong> for unregistered subcontractors</span></li>
                </ul>
                <p className="text-muted-foreground leading-relaxed">
                  You must file monthly CIS returns listing each subcontractor, the amounts paid, and the deductions made. Deductions must be paid over to HMRC. Errors, late returns, or failures to deduct can result in penalties: HMRC takes CIS compliance seriously.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Written scope and price agreements</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  Verbal agreements with subcontractors are an invitation to disputes. Before any subcontractor starts, issue a written scope of works covering exactly what they are to do, the price you have agreed, any programme commitments, how variations will be handled, and your payment terms. This protects both parties and is essential if there is ever a payment dispute.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  For specialist trades (structural steelwork, M&amp;E, groundworks), consider a more formal subcontract based on a standard form: the JCT Short Form of Subcontract is commonly used in the UK residential sector. It's not excessive for contracts above £10,000; it sets clear terms on liability, delay, and variations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Programme management</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  The most costly on-site problem in residential building is idle time: a plasterer ready to start and no board fixed to plaster on. Or a kitchen fitter on site but no M&amp;E first-fix complete. Managing trades in sequence requires a simple programme: even a one-page week-by-week plan is enough to identify clashes and gaps.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Update the programme at the start of each week. Confirm the following week's trades with at least 48–72 hours notice so they can plan their own work. Good programme management is the single biggest factor in keeping subcontractor relationships productive and jobs on programme.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3">Retention</h3>
                <p className="text-muted-foreground leading-relaxed mb-2">
                  Holding a small percentage of each subcontractor payment until the works are complete and snagging is resolved is standard practice. For residential work, retentions of 2.5–5% are typical. Make clear in your subcontract what the retention percentage is, what triggers its release, and the timeframe for payment after release.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Be fair about retention release: holding retention unreasonably is both a relationship issue and, under the Construction Act 1996's payment provisions, potentially actionable. Pay retentions promptly when the defect period has passed and snagging is signed off.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Quoting and cash flow */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="quoting-cash-flow">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">5. Quoting and cash flow</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Quoting and cash flow are the two sides of business health for a UK builder. Quoting accurately determines the margin on every job you take on; cash flow determines whether you can keep taking on jobs at all.
            </p>

            <h3 className="text-xl font-semibold mb-4">Quoting as a competitive tool</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For most small builders, quoting is both the most time-consuming non-site activity and the one with the most impact on business success. A quote that's too high loses the job. A quote that's too low loses money. A quote that arrives three days after a competitor's loses the job regardless of price.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Speed of response is a genuine competitive advantage. Many domestic clients choose the first builder who comes back with a professional, complete quote: not necessarily the cheapest. If your quoting process takes a week, you are losing work to builders who quote the same day.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Using quoting software reduces the time cost of each quote and improves scope completeness: both of which directly affect win rate and job profitability. An AI-assisted tool can take a set of drawings and produce a structured, priced scope in under 10 minutes; manual quoting on the same job typically takes 1–3 hours.
            </p>

            <div className="p-5 rounded-xl bg-primary/5 border border-primary/20 mb-10">
              <p className="font-semibold mb-1">Further reading on quoting</p>
              <div className="space-y-1.5">
                <Link href="/guides/quoting-for-uk-builders/" className="block text-primary hover:underline text-sm">Quoting for UK Builders: the complete guide →</Link>
                <Link href="/blog/how-to-price-building-jobs" className="block text-primary hover:underline text-sm">How to price building jobs: a practical guide for UK builders →</Link>
                <Link href="/blog/what-to-include-in-builders-quote" className="block text-primary hover:underline text-sm">What to include in a builder's quote →</Link>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-4">Cash flow management</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Cash flow is the difference between a profitable building business that grows and one that slowly runs out of money despite winning plenty of work. Many small builders go under not because they are unprofitable, but because they are poorly paid: materials bought, labour paid, jobs completed, but invoices unpaid or stage payments delayed.
            </p>

            <div className="space-y-4 mb-8">
              {[
                {
                  title: "Always take a deposit",
                  body: "A deposit of 10–25% before starting any job provides working capital for materials, demonstrates client commitment, and protects you if the client backs out. Make it clear in your quote that work starts on receipt of the deposit. Clients who resist paying any deposit are often the same ones who are slow to pay the balance.",
                },
                {
                  title: "Agree stage payments upfront",
                  body: "For jobs over £5,000, break the payment schedule into milestones tied to clearly defined stages: foundations complete, roof structure up, first-fix complete, and so on. Include the schedule in your quote and your acceptance paperwork. Stage payments smooth cash flow and give you leverage if a client is slow to pay.",
                },
                {
                  title: "Invoice promptly at each stage",
                  body: "Issue your invoice the day the milestone is reached, not a week later. Delays in invoicing create delays in payment. Set clear payment terms: 7 or 14 days from invoice date is standard for residential clients. Include your bank details, the stage description, and the amount due clearly on every invoice.",
                },
                {
                  title: "Maintain a cash buffer",
                  body: "The target is a cash buffer equivalent to 2–3 months of your overhead costs. This covers you through the gap between winning work and getting paid, through periods when you have fewer jobs running, and through the seasonal slowdown many builders experience in November–January. Build the buffer during busy periods and protect it.",
                },
                {
                  title: "Know your committed costs 3 months forward",
                  body: "At any point in time, you should know what you have committed to spend: materials on order, subcontractors booked, plant hire contracted: over the next 12 weeks, and what income you expect over the same period. The gap between committed spend and expected income is your cash flow risk. If the gap is large, either delay some spend or accelerate some invoicing.",
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl border border-border bg-background">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-semibold mb-3">Late payment: your rights as a contractor</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Under the Late Payment of Commercial Debts Act 1998, you have the right to charge statutory interest (currently 8% above the Bank of England base rate) on overdue commercial invoices. For domestic clients, this does not apply automatically, but you can include late payment terms in your contract.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              For larger contracts with other contractors or commercial clients, the Construction Act 1996 and the Scheme for Construction Contracts give you the right to suspend work for non-payment and to refer payment disputes to adjudication: a fast, cost-effective dispute resolution process. These rights apply to most construction contracts over a certain value; familiarise yourself with them before you need them.
            </p>
          </div>
        </section>

        {/* Related resources */}
        <section className="py-12 border-t border-border/40 bg-muted/20" id="related-resources">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides and tools</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { href: "/guides/quoting-for-uk-builders/", label: "Quoting for UK builders: the complete guide" },
                { href: "/blog/how-to-price-building-jobs", label: "How to price building jobs (UK guide)" },
                { href: "/blog/what-to-include-in-builders-quote", label: "What to include in a builder's quote" },
                { href: "/blog/quoting-tool-for-small-builders", label: "Manual vs automated quoting for builders" },
                { href: "/blog/house-extension-planning-uk", label: "House extension planning in the UK" },
                { href: "/blog/ai-quoting-software-uk-builders", label: "AI quoting software for UK builders" },
                { href: "/for-small-builders", label: "Quoting software for small builders" },
                { href: "/for-sole-traders", label: "Quoting software for sole traders" },
                { href: "/best/quoting-software-for-uk-builders", label: "Best quoting software comparison (2026)" },
                { href: "/ai-quoting-software", label: "AI quoting: how it works" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40" id="faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-0">
              {FAQS.map((faq) => (
                <FaqItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Quote faster, win better jobs</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Proper Tool generates a fully itemised, UK-priced quote from your drawings, photos, or a plain description in under 10 minutes. Take the quoting burden off your evenings.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time · Plans from £29/mo</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
